import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { postOrder, imgUrl, checkPincode, verifyRazorpay } from "@/lib/api";
import { toast } from "sonner";

const Checkout = () => {
  const { items, subtotal, shipping, total, clear } = useCart();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    address_line1: "", address_line2: "",
    city: "", state: "", pincode: "",
    payment_mode: "razorpay", notes: "",
  });
  const [pin, setPin] = useState({ status: "idle", city: null, cod_available: false, estimated_days: "", note: "" });
  const pinTimer = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-check pincode with debounce
  useEffect(() => {
    if (pinTimer.current) clearTimeout(pinTimer.current);
    const p = form.pincode.trim();
    if (p.length !== 6 || !/^\d{6}$/.test(p)) {
      setPin({ status: "idle", city: null, cod_available: false, estimated_days: "", note: "" });
      return;
    }
    setPin((prev) => ({ ...prev, status: "checking" }));
    pinTimer.current = setTimeout(async () => {
      try {
        const r = await checkPincode(p);
        setPin({ status: "ok", ...r });
        // If user was on COD and pincode doesn't support it, flip to razorpay
        setForm((f) => (f.payment_mode === "cod" && !r.cod_available ? { ...f, payment_mode: "razorpay" } : f));
      } catch {
        setPin({ status: "invalid", city: null, cod_available: false, estimated_days: "", note: "Enter a valid 6-digit pincode." });
      }
    }, 400);
    return () => pinTimer.current && clearTimeout(pinTimer.current);
  }, [form.pincode]);

  const openRazorpay = (order) => {
    return new Promise((resolve, reject) => {
      const r = order.razorpay;
      if (!r) return reject(new Error("Missing razorpay payload"));
      // Stub mode (no keys yet): skip real checkout, verify directly
      if (r.stub || !r.key_id) {
        verifyRazorpay({
          order_no: order.order_no,
          razorpay_order_id: r.order_id,
          razorpay_payment_id: `stub_pay_${Date.now()}`,
          razorpay_signature: "stub_signature",
        }).then(resolve).catch(reject);
        return;
      }
      if (!window.Razorpay) return reject(new Error("Razorpay script not loaded"));
      const rzp = new window.Razorpay({
        key: r.key_id,
        amount: r.amount,
        currency: r.currency,
        order_id: r.order_id,
        name: "MELAVI",
        description: `Order ${order.order_no}`,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#AE2B31" },
        handler: async (resp) => {
          try {
            await verifyRazorpay({
              order_no: order.order_no,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            resolve();
          } catch (e) { reject(e); }
        },
        modal: {
          ondismiss: () => reject(new Error("Payment cancelled")),
        },
      });
      rzp.open();
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your cart is empty");
    if (form.pincode.length !== 6) return toast.error("Enter a valid pincode");
    if (form.payment_mode === "cod" && !pin.cod_available) {
      return toast.error("COD not available at this pincode — please use prepaid.");
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty, is_pair: i.is_pair })),
      };
      const order = await postOrder(payload);
      if (form.payment_mode === "razorpay") {
        await openRazorpay(order);
        toast.success("Payment received");
      }
      clear();
      nav(`/order/${order.order_no}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not place the order. Please try again.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const codDisabled = pin.status === "ok" && !pin.cod_available;

  return (
    <div className="pt-32 md:pt-40 pb-32" data-testid="checkout-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 grid md:grid-cols-12 gap-16">
        <div className="md:col-span-7">
          <div className="nav-item mb-4">Checkout</div>
          <h1 className="serif text-4xl md:text-5xl mb-10">Just a few details.</h1>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="checkout-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                { k: "name", l: "Full name", type: "text", full: true },
                { k: "email", l: "Email", type: "email" },
                { k: "phone", l: "Phone", type: "tel" },
                { k: "address_line1", l: "Address line 1", full: true, type: "text" },
                { k: "address_line2", l: "Address line 2 (optional)", full: true, type: "text", required: false },
                { k: "city", l: "City", type: "text" },
                { k: "state", l: "State", type: "text" },
              ].map((f) => (
                <label key={f.k} className={`block ${f.full ? "md:col-span-2" : ""}`}>
                  <span className="nav-item block mb-2">{f.l}</span>
                  <input
                    type={f.type}
                    required={f.required !== false}
                    value={form[f.k]}
                    onChange={(e) => set(f.k, e.target.value)}
                    className="w-full bg-transparent hairline-b px-0 py-3 focus:outline-none focus:border-ink"
                    data-testid={`checkout-${f.k}`}
                  />
                </label>
              ))}
              {/* Pincode with live check */}
              <label className="block">
                <span className="nav-item block mb-2">Pincode</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  value={form.pincode}
                  onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))}
                  className="w-full bg-transparent hairline-b px-0 py-3 focus:outline-none focus:border-ink"
                  data-testid="checkout-pincode"
                />
                <div className="mt-2 text-xs min-h-[16px]" data-testid="pincode-status">
                  {pin.status === "checking" && <span className="text-ink/60">Checking delivery…</span>}
                  {pin.status === "ok" && pin.cod_available && (
                    <span style={{ color: "var(--accent)" }}>✓ {pin.city} · {pin.estimated_days} · COD available</span>
                  )}
                  {pin.status === "ok" && !pin.cod_available && (
                    <span className="text-ink/70">Ships in {pin.estimated_days} · Prepaid only</span>
                  )}
                  {pin.status === "invalid" && <span className="text-ink/70">{pin.note}</span>}
                </div>
              </label>
            </div>

            <div className="pt-4">
              <span className="nav-item block mb-3">Payment</span>
              <div className="flex flex-wrap gap-4">
                <label className={`hairline px-4 py-3 cursor-pointer flex items-center gap-3 ${form.payment_mode === "razorpay" ? "bg-ink text-ground" : ""}`}>
                  <input type="radio" name="pm" checked={form.payment_mode === "razorpay"} onChange={() => set("payment_mode", "razorpay")} className="accent-[#AE2B31]" data-testid="pay-razorpay" />
                  <span className="text-sm tracking-wide">Razorpay · UPI, cards, netbanking</span>
                </label>
                <label
                  className={`hairline px-4 py-3 flex items-center gap-3 ${codDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"} ${form.payment_mode === "cod" && !codDisabled ? "bg-ink text-ground" : ""}`}
                  title={codDisabled ? "Not available at this pincode" : ""}
                >
                  <input
                    type="radio"
                    name="pm"
                    checked={form.payment_mode === "cod"}
                    disabled={codDisabled}
                    onChange={() => !codDisabled && set("payment_mode", "cod")}
                    className="accent-[#AE2B31]"
                    data-testid="pay-cod"
                  />
                  <span className="text-sm tracking-wide">Cash on delivery{codDisabled ? " (unavailable here)" : ""}</span>
                </label>
              </div>
              {form.payment_mode === "razorpay" && (
                <p className="text-xs text-ink/60 mt-3" data-testid="razorpay-note">
                  Secure UPI, cards, netbanking, and wallets via Razorpay.
                </p>
              )}
            </div>

            <button type="submit" disabled={busy || items.length === 0} className="btn-solid" data-testid="place-order-btn">
              {busy ? "Placing…" : `Place order · ₹${total.toLocaleString("en-IN")}`}
            </button>
          </form>
        </div>

        <aside className="md:col-span-5 hairline p-6 h-fit" data-testid="checkout-summary">
          <div className="serif text-2xl mb-6">Your pieces</div>
          {items.length === 0 && <p className="text-sm text-ink/60">Cart is empty.</p>}
          <ul className="space-y-4">
            {items.map((i) => (
              <li key={i.key} className="flex gap-3">
                <img src={imgUrl(i.image)} alt={i.name} className="w-14 h-16 object-cover" />
                <div className="flex-1 text-sm">
                  <div className="serif text-base leading-tight">{i.name}</div>
                  <div className="text-xs text-ink/60">Size {i.size} · Qty {i.qty}{i.is_pair ? " · Pair" : ""}</div>
                </div>
                <div className="text-sm">₹{(i.unit_price * i.qty).toLocaleString("en-IN")}</div>
              </li>
            ))}
          </ul>
          <div className="hairline-t mt-6 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between text-ink/70"><span>Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
            <div className="flex justify-between text-base pt-2"><span>Total</span><span className="serif text-xl">₹{total.toLocaleString("en-IN")}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
